/**
 * The public marketing pages whose components are code-split, made
 * preloadable so a prerendered page never flashes blank.
 *
 * These routes are served with their copy already in the HTML (see
 * `client/src/entry-server.tsx` PRERENDER_ROUTES). React calls `createRoot`,
 * not `hydrateRoot`, so its first commit replaces that copy with whatever it
 * renders — and if the page component is still an unresolved lazy chunk, that
 * is the route-level "Loading page..." spinner. The visitor watches the copy
 * disappear and come back.
 *
 * The fix: `main.tsx` awaits `preloadPublicPageChunk(pathname)` before the
 * first render on these routes. The prerendered HTML stays on screen while
 * the chunk downloads, and React's first commit renders the real page.
 *
 * KEEP IN STEP with PRERENDER_ROUTES: a prerendered route whose component is
 * lazy but missing from PUBLIC_PAGE_PRELOADS flashes again. Eagerly imported
 * pages (CompanyHomePage, FeaturePage) don't need an entry.
 */
import { createPreloadableLazyImport } from "./lazyImports";
import { queryClient } from "@/lib/queryClient";
import {
  buildCursorJobSearchQuery,
  parsePublicJobSearchFilters,
} from "@shared/jobSearch";

export const CandidateHomePage = createPreloadableLazyImport(() => import("@/pages/website/CandidateHomePage"));
export const AboutPage = createPreloadableLazyImport(() => import("@/pages/website/AboutPage"));
export const ContactPage = createPreloadableLazyImport(() => import("@/pages/website/ContactPage"));
export const PricingPage = createPreloadableLazyImport(() => import("@/pages/website/PricingPage"));
export const HiringAppPricingPage = createPreloadableLazyImport(() => import("@/pages/website/HiringAppPricingPage"));
export const AtsPricingPage = createPreloadableLazyImport(() => import("@/pages/website/AtsPricingPage"));
export const CvVsMarketPage = createPreloadableLazyImport(() => import("@/pages/website/CvVsMarketPage"));
export const SampleScorecardPage = createPreloadableLazyImport(() => import("@/pages/website/SampleScorecardPage"));
export const ConnectYourAtsPage = createPreloadableLazyImport(() => import("@/pages/website/ConnectYourAtsPage"));
export const WhatYouGetPage = createPreloadableLazyImport(() => import("@/pages/website/WhatYouGetPage"));
export const HowItWorks = createPreloadableLazyImport(() => import("@/pages/website/HowItWorks"));
export const TermsAndConditions = createPreloadableLazyImport(() => import("@/pages/legal/TermsAndConditions"));
export const PrivacyPolicy = createPreloadableLazyImport(() => import("@/pages/legal/PrivacyPolicy"));
export const CookiePolicy = createPreloadableLazyImport(() => import("@/pages/legal/CookiePolicy"));
export const SecurityPage = createPreloadableLazyImport(() => import("@/pages/legal/SecurityPage"));
export const PublicJobs = createPreloadableLazyImport(() => import("@/pages/public-jobs"));
export const PublicJobDetail = createPreloadableLazyImport(() => import("@/pages/public-job-detail"));

/** Prerendered path → chunk preload, keyed exactly like PRERENDER_ROUTES. */
const PUBLIC_PAGE_PRELOADS: Record<string, () => Promise<void>> = {
  "/candidates": CandidateHomePage.preload,
  "/company/about": AboutPage.preload,
  "/company/contact": ContactPage.preload,
  "/company/pricing": PricingPage.preload,
  "/company/pricing/hiring-app": HiringAppPricingPage.preload,
  "/company/pricing/ats-layer": AtsPricingPage.preload,
  "/cv-vs-market": CvVsMarketPage.preload,
  "/sample-scorecard": SampleScorecardPage.preload,
  "/connect-your-ats": ConnectYourAtsPage.preload,
  "/what-you-get": WhatYouGetPage.preload,
  "/how-it-works": HowItWorks.preload,
  "/legal/terms": TermsAndConditions.preload,
  "/legal/privacy": PrivacyPolicy.preload,
  "/legal/cookies": CookiePolicy.preload,
  "/legal/security": SecurityPage.preload,
  "/jobs": PublicJobs.preload,
};

/**
 * Starts (and returns) the page-chunk download for a prerendered public path,
 * or null when the path's component is eager / not prerendered.
 */
export function preloadPublicPageChunk(rawPath: string): Promise<void> | null {
  let path = rawPath.split(/[?#]/)[0] ?? rawPath;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  if (/^\/jobs\/[^/]+$/.test(path)) return PublicJobDetail.preload();
  const preload = PUBLIC_PAGE_PRELOADS[path];
  return preload ? preload() : null;
}

/**
 * Warms the public job query while its route chunk downloads. The server's
 * crawlable job body can then stay in place until React's first commit renders
 * the complete styled page, rather than replacing it with a blank query state.
 */
export function preloadPublicJobData(rawPath: string): Promise<void> | null {
  const search = rawPath.includes('?')
    ? (rawPath.slice(rawPath.indexOf('?') + 1).split('#')[0] ?? '')
    : '';
  let path = rawPath.split(/[?#]/)[0] ?? rawPath;
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);

  if (path === '/jobs') {
    const filters = parsePublicJobSearchFilters(search);
    return queryClient.prefetchInfiniteQuery({
      queryKey: ['jobs', 'infinite', filters],
      queryFn: async ({ pageParam }) => {
        const queryString = buildCursorJobSearchQuery(filters, pageParam, 20);
        const response = await fetch(`/api/jobs/search?${queryString}`);
        if (!response.ok) throw new Error('Failed to fetch jobs');
        return response.json();
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage: { hasMore: boolean; nextCursor?: string }) =>
        lastPage.hasMore ? lastPage.nextCursor : undefined,
      staleTime: 5 * 60 * 1000,
    });
  }

  const match = path.match(/^\/jobs\/([^/]+)$/);
  if (!match?.[1]) return null;

  const queryPath = `/api/jobs/public/slug/${match[1]}`;
  return queryClient.prefetchQuery({
    queryKey: [queryPath],
    staleTime: 30_000,
  });
}
